import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { deletePartner } from '@/data/partnersRepo';
import { usePartners } from '@/hooks/usePartners';
import { type PartnerType } from '@/types/types_partner';

/** Admin list of partners, styled like the original admin.html table. */
export function PagePartnerList() {
  const { partners, loading } = usePartners();
  const [toDelete, setToDelete] = useState<PartnerType | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function confirmDelete() {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deletePartner(toDelete.id);
      setToDelete(null);
      // The snapshot listener removes the row automatically.
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <header className='tw:mb-6 tw:flex tw:items-center tw:justify-between'>
        <div>
          <h1 className='tw:font-serif tw:text-2xl tw:text-ed-black'>Partners</h1>
          <p className='tw:mt-1 tw:text-sm tw:text-ed-stone'>
            Companies and groups that book experiences for their guests. {partners.length} partners.
          </p>
        </div>
        <Link
          to='/admin/partners/new'
          className='tw:flex tw:items-center tw:gap-2 tw:bg-ed-gold tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:text-ed-black tw:transition-colors tw:hover:bg-ed-black tw:hover:text-ed-cream'
        >
          <Plus className='tw:size-4' /> Register New Partner
        </Link>
      </header>

      <div className='tw:overflow-x-auto tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:shadow-sm'>
        <table className='tw:w-full'>
          <thead className='tw:bg-ed-cream/50'>
            <tr>
              {['Partner Name', 'Contact', ''].map((h) => (
                <th
                  key={h}
                  className='tw:px-6 tw:py-3 tw:text-left tw:text-xs tw:font-medium tw:tracking-wider tw:text-ed-stone tw:uppercase'
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className='tw:px-6 tw:py-10 tw:text-center tw:text-ed-stone'>
                  Loading…
                </td>
              </tr>
            ) : partners.length === 0 ? (
              <tr>
                <td colSpan={3} className='tw:px-6 tw:py-10 tw:text-center tw:text-ed-stone'>
                  No partners yet.
                </td>
              </tr>
            ) : (
              partners.map((partner) => (
                <tr key={partner.id} className='tw:border-t tw:border-ed-umber/10 tw:hover:bg-ed-cream/20'>
                  <td className='tw:px-6 tw:py-4 tw:text-sm tw:font-medium tw:text-ed-black'>
                    {partner.name}
                    {partner.brand && <div className='tw:text-xs tw:text-ed-stone'>{partner.brand}</div>}
                  </td>
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>
                    {partner.contactName}
                    <div className='tw:text-xs tw:text-ed-stone'>{partner.contactEmail}</div>
                  </td>
                  <td className='tw:px-6 tw:py-4'>
                    <div className='tw:flex tw:justify-end tw:gap-2'>
                      <Link
                        to={`/admin/partners/${partner.id}/edit`}
                        title='Edit'
                        className='tw:border tw:border-ed-umber/30 tw:p-2 tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-gold tw:hover:text-ed-gold'
                      >
                        <Pencil className='tw:size-4' />
                      </Link>
                      <button
                        type='button'
                        title='Delete'
                        onClick={() => setToDelete(partner)}
                        className='tw:border tw:border-ed-umber/30 tw:p-2 tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-red tw:hover:text-ed-red'
                      >
                        <Trash2 className='tw:size-4' />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!toDelete} onOpenChange={(open) => !open && !deleting && setToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete partner?</DialogTitle>
            <DialogDescription>
              This will remove &ldquo;{toDelete?.name}&rdquo;. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' disabled={deleting} onClick={() => setToDelete(null)}>
              Cancel
            </Button>
            <Button variant='destructive' loading={deleting} onClick={() => void confirmDelete()}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
